import { useDataEngine } from "@dhis2/app-runtime";

const RATING_SCALE_NAMESPACE = 'leadership-rating-scale';

export const DEFAULT_RATING_SCALE = [
  { value: 1, label: '1 - Unsatisfactory', color: '#dc3545', description: 'Rarely meets expectations' },
  { value: 2, label: '2 - Developing', color: '#ff9800', description: 'Sometimes meets expectations; improvement needed' },
  { value: 3, label: '3 - Competent', color: '#ffc107', description: 'Meets expectations consistently' },
  { value: 4, label: '4 - Strong Performer', color: '#4caf50', description: 'Frequently exceeds expectations' },
  { value: 5, label: '5 - Role Model', color: '#28a745', description: 'Consistently exceeds expectations; sets example for others' }
];

// Helper to get parent org unit ID from hierarchy
const getParentOrgUnitId = async (engine, orgUnitId) => {
  if (!orgUnitId) return null;
  
  try {
    const response = await engine.query({
      orgUnit: {
        resource: `organisationUnits/${orgUnitId}`,
        params: {
          fields: 'path,parent[id]'
        }
      }
    });
    
    const path = response.orgUnit?.path;
    if (path) {
      const pathParts = path.split('/').filter(Boolean);
      // Level 5 is at index 4 (0-based: 0=root, 1=level2, 2=level3, 3=level4, 4=level5)
      if (pathParts.length >= 5) {
        return pathParts[4];
      }
      return response.orgUnit?.parent?.id || null;
    }
    return null;
  } catch (error) {
    console.error('Failed to get parent org unit:', error);
    return null;
  }
};

export function useRatingScaleService(orgUnitId) {
  const engine = useDataEngine();

  const getDataStoreKey = (orgId) => {
    if (orgId) {
      return `scale_${orgId}`;
    }
    return 'scale_global';
  };

  const loadRatingScale = async () => {
    if (!orgUnitId) {
      return DEFAULT_RATING_SCALE;
    }

    // Get the parent (Level 5) org unit
    const parentOrgId = await getParentOrgUnitId(engine, orgUnitId);
    
    // Priority: Current org -> Parent org (Level 5) -> Global -> Default
    const orgsToCheck = [orgUnitId];
    if (parentOrgId && parentOrgId !== orgUnitId) {
      orgsToCheck.push(parentOrgId);
    }

    // Try each org in hierarchy
    for (const orgId of orgsToCheck) {
      const key = getDataStoreKey(orgId);
      try {
        const response = await engine.query({
          ratingScale: {
            resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`,
          }
        });
        
        if (response.ratingScale && Array.isArray(response.ratingScale)) {
          return response.ratingScale;
        }
      } catch (error) {
        // Continue to next org
      }
    }
    
    // Try global scale
    try {
      const globalResponse = await engine.query({
        ratingScale: {
          resource: `dataStore/${RATING_SCALE_NAMESPACE}/scale_global`,
        }
      });
      if (globalResponse.ratingScale && Array.isArray(globalResponse.ratingScale)) {
        return globalResponse.ratingScale;
      }
    } catch (globalError) {
      // Use default
    }
    
    return DEFAULT_RATING_SCALE;
  };

  const saveRatingScale = async (ratingScale) => {
    if (!ratingScale || !Array.isArray(ratingScale)) {
      return { success: false, error: 'Invalid rating scale data' };
    }
    
    if (!orgUnitId) {
      return { success: false, error: 'No organisation unit selected' };
    }
    
    const key = getDataStoreKey(orgUnitId);
    try {
      let exists = false;
      try {
        await engine.query({
          check: {
            resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`,
          }
        });
        exists = true;
      } catch (e) {
        exists = false;
      }

      if (exists) {
        await engine.mutate({
          resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`,
          type: 'update',
          data: ratingScale
        });
      } else {
        await engine.mutate({
          resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`,
          type: 'create',
          data: ratingScale
        });
      }
      return { success: true };
    } catch (error) {
      console.error('Failed to save rating scale:', error);
      return { success: false, error: error.message };
    }
  };

  const getRatingLabel = (value, ratingScale) => {
    if (!ratingScale || !Array.isArray(ratingScale)) return `${value} - Unknown`;
    const rating = ratingScale.find(r => r && r.value === value);
    return rating ? rating.label : `${value} - Unknown`;
  };

  const getRatingColor = (value, ratingScale) => {
    if (!ratingScale || !Array.isArray(ratingScale)) return '#6c757d';
    const rating = ratingScale.find(r => r && r.value === value);
    return rating ? rating.color : '#6c757d';
  };

  return {
    loadRatingScale,
    saveRatingScale,
    getRatingLabel,
    getRatingColor,
    DEFAULT_RATING_SCALE
  };
}